// Inline creation of a master product family from inside the item wizard, so
// ops never leaves the inquiry flow. Two fields — universal name (prefilled
// with what they typed into the search) and catalog category — exactly what
// POST master-product-families requires. Errors render inline (not a toast)
// so the typed values survive a failed attempt.

import { MasterProductFamily, useCreateMasterProductFamilyApi } from 'apis/master-product-families';
import { CategoryModal } from 'components/category-picker/category-modal';
import type { CategoryRef } from 'components/category-picker/types';
import { Box, Button, Flex, FormControl, FormLabel, Input, Text } from 'design-system';
import { apiErrorMessage } from 'pages/inquiry-processor/utils/inquiry-actions';
import { useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';

interface InlineFamilyFormProps {
  initialName: string;
  onCreated: (family: MasterProductFamily) => void;
  onCancel: () => void;
}

export const InlineFamilyForm = ({ initialName, onCreated, onCancel }: InlineFamilyFormProps) => {
  const createFamily = useCreateMasterProductFamilyApi();
  const [name, setName] = useState(initialName);
  const [category, setCategory] = useState<CategoryRef | null>(null);
  const [error, setError] = useState('');

  const save = async () => {
    if (!name.trim() || !category) return;
    setError('');
    try {
      const family = await createFamily.mutateAsync({ universalName: name.trim(), catalogCategoryId: category.id });
      onCreated(family);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  return (
    <Box borderWidth='1px' borderColor='red.200' rounded='md' p='3' bg='red.50'>
      <Text fontWeight='semibold' fontSize='sm' mb='1'>
        New master product family
      </Text>
      <Text fontSize='xs' color='gray.600' mb='2'>
        This creates a master catalog entry visible everywhere.
      </Text>
      <Flex gap='3' align='flex-end' wrap='wrap'>
        <FormControl isRequired maxW='320px'>
          <FormLabel fontSize='xs' mb='1'>
            Universal name
          </FormLabel>
          <Input
            size='sm'
            bg='white'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='e.g. Whole Chicken'
          />
        </FormControl>
        <FormControl isRequired maxW='320px'>
          <FormLabel fontSize='xs' mb='1'>
            Category
          </FormLabel>
          <CategoryModal
            value={category}
            onSelect={setCategory}
            trigger={
              <Button
                size='sm'
                variant='outline'
                w='100%'
                bg='white'
                justifyContent='space-between'
                fontWeight='normal'
                color={category ? 'gray.800' : 'gray.400'}
                rightIcon={<FiChevronDown />}>
                <Box as='span' isTruncated>
                  {category ? category.title : '— Select category —'}
                </Box>
              </Button>
            }
          />
        </FormControl>
        {/* Wraps within the form: in a narrow host (the Prices family panel)
            each button takes its own full-width line instead of spilling
            past the card; in the wide item board they sit side by side. */}
        <Flex gap='2' wrap='wrap' maxW='100%'>
          <Button size='sm' variant='outline' flex='1 1 auto' onClick={onCancel} isDisabled={createFamily.isLoading}>
            Cancel
          </Button>
          <Button
            size='sm'
            colorScheme='red'
            flex='1 1 auto'
            onClick={save}
            isLoading={createFamily.isLoading}
            loadingText='Creating…'
            isDisabled={!name.trim() || !category}>
            Create family
          </Button>
        </Flex>
      </Flex>
      {error && (
        <Text fontSize='xs' color='red.500' mt='2' whiteSpace='pre-wrap'>
          {error}
        </Text>
      )}
    </Box>
  );
};
